import { useId, useState } from 'react'
import type { JSX } from 'react'

import {
  CheckIcon,
  Flex,
  Form,
  Input,
  Text,
  WarningIcon,
} from '@buffer-mono/popcorn'

import styles from '../index.module.css'
import { CancelFlowStepFrame } from './CancelFlowStepFrame'
import { formatPeriodEnd } from './formatPeriodEnd'

type ConfirmStepProps = {
  planName: string
  periodEnd: string | null
  isLoading: boolean
  isTrialCancellation: boolean
  keptChannelCount: number
  deactivatedChannelCount: number
  wasChannelSelectionShown: boolean
  stepLabel?: string
  onBack?: () => void
  onDismiss: () => void
  onConfirm: () => Promise<void>
}

export const ConfirmStep = ({
  planName,
  periodEnd,
  isLoading,
  isTrialCancellation,
  keptChannelCount,
  deactivatedChannelCount,
  wasChannelSelectionShown,
  stepLabel,
  onBack,
  onDismiss,
  onConfirm,
}: ConfirmStepProps): JSX.Element => {
  const [confirmText, setConfirmText] = useState('')
  const confirmationErrorId = useId()
  const formattedPeriodEnd = formatPeriodEnd(periodEnd)
  const planLabel = planName.trim() || 'paid'
  const isConfirmed = confirmText === 'confirm'
  const showConfirmationError =
    confirmText.length >= 'confirm'.length && !isConfirmed
  const keptChannelLabel =
    keptChannelCount === 1 ? 'channel stays' : 'channels stay'
  const deactivatedChannelLabel =
    deactivatedChannelCount === 1 ? 'channel' : 'channels'
  const channelSummarySubject = wasChannelSelectionShown
    ? 'selected'
    : 'connected'
  const channelSummary =
    keptChannelCount > 0
      ? `${keptChannelCount} ${channelSummarySubject} ${keptChannelLabel} active on the Free plan.`
      : 'No active channels will be affected.'

  return (
    <CancelFlowStepFrame
      isLoading={isLoading}
      onBack={onBack}
      stepLabel={stepLabel}
      secondaryAction={{
        children: isTrialCancellation ? 'Keep My Trial' : 'Keep My Plan',
        onClick: onDismiss,
        variant: 'tertiary',
      }}
      primaryAction={{
        children: isTrialCancellation ? 'Cancel Trial' : 'Confirm Cancellation',
        disabled: !isConfirmed,
        loading: isLoading,
        onClick: onConfirm,
        tooltip: !isConfirmed ? 'Type "confirm" to continue' : undefined,
        variant: 'critical',
      }}
      title="Review Your Cancellation"
      contentClassName={styles.confirmContent}
      description={
        isTrialCancellation ? (
          <>Your trial will end and your account will move to the Free plan.</>
        ) : formattedPeriodEnd ? (
          <>
            Your <strong>{planLabel}</strong> plan stays active until{' '}
            <strong>{formattedPeriodEnd}</strong>, then your account moves to
            the Free plan.
          </>
        ) : (
          <>
            Your <strong>{planLabel}</strong> plan stays active until the end of
            your current billing period, then your account moves to the Free
            plan.
          </>
        )
      }
      descriptionProps={{ align: 'left', color: 'subtle' }}
      descriptionWrapperClassName={styles.confirmDetails}
      contentGap="xl"
    >
      <div className={styles.confirmContainer}>
        <div className={styles.confirmCard}>
          <Flex direction="column" gap="xs">
            <Text weight="medium">Changes to Your Account</Text>
            <Flex
              as="ul"
              direction="column"
              gap="xs"
              className={styles.confirmSummary}
            >
              {!isTrialCancellation && (
                <li className={styles.confirmSummaryItem}>
                  <Text icon={<CheckIcon color="success" />}>
                    Full access continues until{' '}
                    {formattedPeriodEnd ?? 'your billing period ends'}.
                  </Text>
                </li>
              )}
              <li className={styles.confirmSummaryItem}>
                <Text icon={<CheckIcon color="success" />}>
                  {channelSummary}
                </Text>
              </li>
              {deactivatedChannelCount > 0 && (
                <li className={styles.confirmSummaryItem}>
                  <Text icon={<WarningIcon color="warning" />}>
                    {deactivatedChannelCount} {deactivatedChannelLabel} will be
                    deactivated.
                  </Text>
                </li>
              )}
              <li className={styles.confirmSummaryItem}>
                <Text icon={<WarningIcon color="warning" />}>
                  Paid features will no longer be available on the Free plan.
                </Text>
              </li>
            </Flex>
          </Flex>
          <Form.Field name="confirmCancellation" className={styles.confirmGate}>
            <Form.Label>
              Type &ldquo;confirm&rdquo; to{' '}
              {isTrialCancellation
                ? 'cancel your trial'
                : 'proceed with cancellation'}
            </Form.Label>
            <Form.Control>
              <Input
                className={styles.confirmInput}
                placeholder="confirm"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                aria-describedby={
                  showConfirmationError ? confirmationErrorId : undefined
                }
                aria-invalid={showConfirmationError}
              />
            </Form.Control>
            {showConfirmationError && (
              <Text
                color="critical"
                size="sm"
                role="alert"
                id={confirmationErrorId}
              >
                Please type &ldquo;confirm&rdquo; exactly as shown.
              </Text>
            )}
          </Form.Field>
        </div>
      </div>
    </CancelFlowStepFrame>
  )
}
